/**
 * <s-variant-sync> — Client-side variant switching for product forms rendered outside the
 * product template (for example a one-product homepage), where a ?variant= partial refetch
 * cannot re-select the variant server-side.
 *
 * Required child markup:
 *   input[data-variant-id]                  — Option radios; the checked one drives the variant.
 *   input[name="id"]                        — Hidden product-form input kept in sync for add-to-cart and Buy now.
 *   template[data-variant-price="<id>"]     — Pre-rendered price markup for each variant.
 *   [data-price-target]                     — Live region that receives the selected variant's price.
 *   button[name="add"]                      — Add-to-cart submitter toggled by availability.
 *
 * Element dataset:
 *   data-available-ids — Comma-separated ids of variants that can be bought.
 *   data-add-label / data-sold-out-label — Button labels for each availability state.
 */
class VariantSync extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('change', (event) => {
      const variantId = event.target?.dataset?.variantId;
      if (variantId) this.#select(variantId);
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #select(variantId) {
    const idInput = this.querySelector('input[name="id"]');
    if (idInput) {
      idInput.value = variantId;
      // Accelerated checkout buttons read the form, so let them observe the change.
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const priceTemplate = this.querySelector(`template[data-variant-price="${CSS.escape(variantId)}"]`);
    const priceTarget = this.querySelector('[data-price-target]');
    if (priceTemplate && priceTarget) {
      priceTarget.replaceChildren(priceTemplate.content.cloneNode(true));
    }

    const available = (this.dataset.availableIds || '').split(',').includes(variantId);
    const addButton = this.querySelector('button[name="add"]');
    if (addButton) {
      addButton.type = available ? 'submit' : 'button';
      addButton.setAttribute('aria-disabled', String(!available));
      addButton.classList.toggle('opacity-50', !available);
      const label = available ? this.dataset.addLabel : this.dataset.soldOutLabel;
      if (label) addButton.textContent = label;
    }
  }
}
customElements.define('s-variant-sync', VariantSync);
